
import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, X } from 'react-feather';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      id: 'free',
      name: t('home.pricing.free.title'),
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        t('home.pricing.free.feature1'),
        t('home.pricing.free.feature2'),
        t('home.pricing.free.feature3'),
      ],
      included: ['events', 'basicAnalytics', 'standardSupport'],
      notIncluded: ['prioritySupport', 'customBranding', 'advancedAnalytics', 'verifiedBadge']
    },
    {
      id: 'premium',
      name: t('home.pricing.premium.title'),
      monthlyPrice: 5.99,
      annualPrice: 59.99,
      popular: true,
      features: [
        t('home.pricing.premium.feature1'),
        t('home.pricing.premium.feature2'),
        t('home.pricing.premium.feature3'),
      ],
      included: ['events', 'basicAnalytics', 'standardSupport', 'prioritySupport', 'customBranding', 'advancedAnalytics'],
      notIncluded: ['verifiedBadge']
    },
    {
      id: 'expert',
      name: 'Expert',
      monthlyPrice: 9.99,
      annualPrice: 99.99,
      features: [
        t('home.pricing.premium.feature1'),
        t('home.pricing.premium.feature2'),
        t('home.pricing.premium.feature3'),
        t('home.pricing.premium.feature4'),
      ],
      included: ['events', 'basicAnalytics', 'standardSupport', 'prioritySupport', 'customBranding', 'advancedAnalytics', 'verifiedBadge'],
      notIncluded: []
    }
  ];

  const allFeatures = [
    { id: 'events', label: 'Events per month', free: '7 events', premium: 'Unlimited', expert: 'Unlimited' },
    { id: 'basicAnalytics', label: 'Basic Analytics', free: true, premium: true, expert: true },
    { id: 'standardSupport', label: 'Standard Support', free: true, premium: true, expert: true },
    { id: 'prioritySupport', label: 'Priority Support', free: false, premium: true, expert: true },
    { id: 'customBranding', label: 'Custom Event Branding', free: false, premium: true, expert: true },
    { id: 'advancedAnalytics', label: 'Advanced Analytics', free: false, premium: true, expert: true },
    { id: 'verifiedBadge', label: 'Verified DJ Badge', free: false, premium: false, expert: true },
  ];

  const getSavings = (monthlyPrice: number, annualPrice: number) => {
    if (monthlyPrice === 0) return 0;
    const monthlyCost = monthlyPrice * 12;
    const savings = monthlyCost - annualPrice;
    return Math.round((savings / monthlyCost) * 100);
  };

  return (
    <Container className="py-5">
      {/* Header */}
      <Row className="text-center mb-5">
        <Col>
          <h1 className="display-4 fw-bold mb-3">{t('home.pricing.title')}</h1>
          <p className="lead text-muted mb-4">{t('home.pricing.subtitle')}</p>
          
          {/* Monthly/Annual Toggle */}
          <div className="d-flex justify-content-center align-items-center mb-4">
            <span className={`me-3 ${!isAnnual ? 'fw-bold' : 'text-muted'}`}>Monthly</span>
            <Form.Check
              type="switch"
              id="pricing-toggle"
              checked={isAnnual}
              onChange={(e) => setIsAnnual(e.target.checked)}
              className="mx-2"
            />
            <span className={`ms-3 ${isAnnual ? 'fw-bold' : 'text-muted'}`}>
              Annual
              <span className="badge bg-success ms-2">Save up to 17%</span>
            </span>
          </div>
        </Col>
      </Row>

      {/* Pricing Cards */}
      <Row className="justify-content-center g-4 mb-5">
        {plans.map((plan) => (
          <Col key={plan.id} md={6} lg={4}>
            <Card className={`h-100 ${plan.popular ? 'border-primary shadow' : 'shadow-sm'}`}>
              {plan.popular && (
                <div className="bg-primary text-white text-center py-2">
                  <span className="fw-bold">{t('home.pricing.popular')}</span>
                </div>
              )}
              
              <Card.Body className="p-4 text-center">
                <Card.Title className="fw-bold mb-1">{plan.name}</Card.Title>
                
                <div className="price-section my-4">
                  <div className="price-display">
                    <span className="h1 fw-bold">
                      €{isAnnual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-muted">
                      /{isAnnual ? 'year' : t('home.pricing.month')}
                    </span>
                  </div>
                  
                  {isAnnual && plan.monthlyPrice > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        €{(plan.annualPrice / 12).toFixed(2)}/month billed annually
                      </small>
                      <div className="badge bg-success-subtle text-success mt-1">
                        Save {getSavings(plan.monthlyPrice, plan.annualPrice)}%
                      </div>
                    </div>
                  )}
                </div>

                <ul className="list-unstyled text-start mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="d-grid">
                  <Button
                    as={Link as unknown as any}
                    to="/signup"
                    variant={plan.popular ? "primary" : "outline-primary"}
                    className={plan.popular ? "custom-purple-btn" : ""}
                  >
                    {t('home.pricing.free.button')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Feature Comparison Table */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white py-3">
              <h3 className="mb-0 text-center">Compare Plans</h3>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 py-3 ps-4">Features</th>
                      <th className="border-0 py-3 text-center">Free</th>
                      <th className="border-0 py-3 text-center position-relative">
                        Premium
                        <span className="badge bg-primary position-absolute top-0 start-50 translate-middle mt-n2">
                          Popular
                        </span>
                      </th>
                      <th className="border-0 py-3 text-center">Expert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allFeatures.map((feature) => (
                      <tr key={feature.id}>
                        <td className="py-3 ps-4 fw-medium">{feature.label}</td>
                        <td className="py-3 text-center">
                          {typeof feature.free === 'boolean' ? (
                            feature.free ? (
                              <Check className="text-success" size={20} />
                            ) : (
                              <X className="text-muted" size={20} />
                            )
                          ) : (
                            <span className="text-muted">{feature.free}</span>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          {typeof feature.premium === 'boolean' ? (
                            feature.premium ? (
                              <Check className="text-success" size={20} />
                            ) : (
                              <X className="text-muted" size={20} />
                            )
                          ) : (
                            <span className="text-success fw-medium">{feature.premium}</span>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          {typeof feature.expert === 'boolean' ? (
                            feature.expert ? (
                              <Check className="text-success" size={20} />
                            ) : (
                              <X className="text-muted" size={20} />
                            )
                          ) : (
                            <span className="text-success fw-medium">{feature.expert}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* FAQ Section */}
      <Row className="mt-5">
        <Col>
          <div className="text-center mb-4">
            <h3 className="fw-bold">Frequently Asked Questions</h3>
          </div>
          <Row>
            <Col md={6}>
              <div className="mb-4">
                <h5 className="fw-bold">Can I switch plans anytime?</h5>
                <p className="text-muted">Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.</p>
              </div>
              <div className="mb-4">
                <h5 className="fw-bold">What payment methods do you accept?</h5>
                <p className="text-muted">We accept all major credit cards including Visa, MasterCard, and American Express.</p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-4">
                <h5 className="fw-bold">Is there a free trial?</h5>
                <p className="text-muted">Yes! Our Free plan allows you to try our platform with up to 7 events per month at no cost.</p>
              </div>
              <div className="mb-4">
                <h5 className="fw-bold">Do annual plans save money?</h5>
                <p className="text-muted">Yes, annual plans offer significant savings - up to 17% compared to monthly billing.</p>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* CTA Section */}
      <Row className="mt-5">
        <Col className="text-center">
          <div className="bg-light rounded p-5">
            <h3 className="fw-bold mb-3">Ready to get started?</h3>
            <p className="lead text-muted mb-4">Join thousands of DJs who trust MyWeeshy for their events</p>
            <Button
              as={Link as unknown as any}
              to="/signup"
              variant="primary"
              size="lg"
              className="custom-purple-btn"
            >
              Start Free Trial
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PricingPage;
